/**
 * WishlistList Web Component
 *
 * Legge i prodotti in wishlist dall'SDK WishListHero e renderizza
 * una griglia di card prodotto usando il template view "wishlist".
 *
 * Endpoint: GET /products/{handle}?view=wishlist&variant={variantId}
 * Richiede: templates/product.card.json con "layout": false
 *
 * Basato sullo stesso pattern di WishlistButton (wishlist-button.js).
 */
class WishlistList extends HTMLElement {
  connectedCallback() {
    this._grid = this.querySelector(".wishlist-list__grid");
    this._loading = this.querySelector(".wishlist-list__loading");
    this._empty = this.querySelector(".wishlist-list__empty");
    this._emptyText =
      this.getAttribute("data-empty-text") || "La tua wishlist è vuota.";

    // Mappa handle → elemento DOM della card (per rimozione live)
    this._cardsByVariantId = new Map();

    // Ascolta la rimozione live di prodotti dagli altri bottoni wishlist
    document.addEventListener("wishlist:updated", (e) => {
      if (!e.detail.inWishlist) {
        this._removeCard(String(e.detail.variantId));
      }
    });

    if (window.WishListHero_SDK) {
      this._loadWishlist();
    } else {
      document.addEventListener(
        "wishlist-hero-wishlist-sdk-ready",
        () => this._loadWishlist(),
        { once: true },
      );
    }
  }

  /**
   * Recupera gli item dalla wishlist e avvia il rendering delle card.
   */
  async _loadWishlist() {
    this._showLoading(true);

    try {
      const items = window.WishListHero_SDK.GetWishListItems() || [];

      if (items.length === 0) {
        this._showEmpty();
        return;
      }

      // Deduplicazione per Variant ID (ogni variante/taglia avrà la sua card)
      const seen = new Set();
      const uniqueItems = items.filter((item) => {
        const variantId = String(item.ProductVariantId || item.variantId || "");
        if (!variantId || seen.has(variantId)) return false;
        seen.add(variantId);
        return true;
      });

      // Fetch parallele delle card via Section Rendering API
      const htmlParts = await Promise.all(
        uniqueItems.map((item) => this._fetchCard(item)),
      );

      // Popola la griglia
      htmlParts.forEach((html, index) => {
        if (!html) return;
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        const variantId = String(
          uniqueItems[index].ProductVariantId ||
            uniqueItems[index].variantId ||
            "",
        );
        if (variantId) {
          this._cardsByVariantId.set(variantId, wrapper);
        }
        this._grid.appendChild(wrapper);
      });

      const hasCards = this._grid.children.length > 0;
      if (hasCards) {
        this._showGrid();
      } else {
        this._showEmpty();
      }
    } catch (err) {
      console.error("[WishlistList] Errore durante il caricamento:", err);
      this._showEmpty();
    }
  }

  /**
   * Recupera l'HTML della card per un singolo item tramite il template view "wishlist".
   * Usa: GET /products/{handle}?view=wishlist&variant={variantId}
   * La risposta è HTML diretto grazie a "layout": false nel template.
   *
   * @param {Object} item - Item della wishlist con ProductHandle/ProductLink e ProductVariantId
   * @returns {Promise<string|null>} HTML della card oppure null in caso di errore
   */
  async _fetchCard(item) {
    const handle = this._resolveHandle(item);
    if (!handle) {
      console.warn("[WishlistList] Handle non trovato per l'item:", item);
      return null;
    }

    const variantId =
      item.ProductVariantId || item.variantId || "";

    try {
      const url = `/products/${handle}?view=card&variant=${variantId}`;
      const res = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!res.ok) {
        console.warn(`[WishlistList] Fetch fallita per handle "${handle}":`, res.status);
        return null;
      }

      // Il template usa "layout": false → risposta HTML diretta (non JSON)
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const card = doc.querySelector(".product_card");
      return card ? card.outerHTML : null;
    } catch (err) {
      console.warn(`[WishlistList] Errore fetch per handle "${handle}":`, err);
      return null;
    }
  }

  /**
   * Estrae l'handle del prodotto dall'item della wishlist.
   * WishListHero può fornirlo come ProductHandle oppure ricavarlo da ProductLink.
   */
  _resolveHandle(item) {
    if (item.ProductHandle) return item.ProductHandle;

    // Fallback: estrae l'handle dall'URL del prodotto
    const link = item.ProductLink || item.productLink || "";
    if (link) {
      const match = link.match(/\/products\/([^/?#]+)/);
      if (match) return match[1];
    }

    return null;
  }

  /**
   * Rimuove la card di un prodotto dalla griglia (dopo wishlist:updated).
   * @param {string} variantId
   */
  _removeCard(variantId) {
    const wrapper = this._cardsByVariantId.get(variantId);
    if (wrapper) {
      wrapper.remove();
      this._cardsByVariantId.delete(variantId);
    }

    if (this._grid.children.length === 0) {
      this._showEmpty();
    }
  }

  // ── Helpers di stato UI ──────────────────────────────────────────────────

  _showLoading(show) {
    if (this._loading) this._loading.hidden = !show;
    if (this._grid) this._grid.hidden = true;
    if (this._empty) this._empty.hidden = true;
  }

  _showGrid() {
    if (this._loading) this._loading.hidden = true;
    if (this._grid) this._grid.hidden = false;
    if (this._empty) this._empty.hidden = true;
  }

  _showEmpty() {
    if (this._loading) this._loading.hidden = true;
    if (this._grid) this._grid.hidden = true;
    if (this._empty) this._empty.hidden = false;
  }
}

if (!customElements.get("wishlist-list")) {
  customElements.define("wishlist-list", WishlistList);
}